import { z } from "zod";

/**
 * Shared DTO cho Admin Payments & Gateway Operations (US-AE6.F1-0021):
 *  - @UC9050 gateway-global-toggle — PATCH /admin/payments/gateway (bật/tắt gateway_auto toàn cục).
 *  - @UC9051 webhook-monitor       — GET   /admin/payments/webhook-events (giám sát verify/dedupe/latency).
 *
 * Một nguồn sự thật shape cho api (S02) + admin web (S04) — tránh drift DTO ở 2 nơi (DRY).
 *
 * BẢO MẬT / BẤT BIẾN (invariants):
 * - Toggle mode enum = allowed của `payment_confirmation_mode` (funds.ts): two_party | admin_one_party
 *   | gateway_auto — KHÔNG thêm mode mới. Tắt gateway = set về manual mode hợp lệ.
 * - Webhook-event DTO READ-ONLY, KHÔNG payload/secret thô (chỉ payloadHash) — chống leak PII (C2/G4).
 * - Money = BigInt minor units serialize STRING trên wire (B1 no-float). amountMinor = string | null.
 * - `.strict()` — reject field lạ tại biên. Cursor opaque string đối xứng common/pagination/cursor.
 * - G2: DTO chỉ mô tả kết quả ĐỌC (monitor) + toggle config — KHÔNG field mutating ledger.
 */

// ── Gateway toggle (@UC9050) ────────────────────────────────────────────────

/**
 * Chế độ xác nhận thanh toán toàn cục (allowed của system_settings.payment_confirmation_mode).
 * Toggle gateway = chuyển `gateway_auto` ↔ manual (two_party / admin_one_party). Bám EPICS:118 —
 * KHÔNG thêm mode mới ngoài allowed.
 */
export const gatewayModeSchema = z.enum(["two_party", "admin_one_party", "gateway_auto"]);
export type GatewayMode = z.infer<typeof gatewayModeSchema>;

/** Độ dài tối đa `reason` (audit) — chống payload rác/DoS text. */
export const GATEWAY_TOGGLE_REASON_MAX_LENGTH = 500;

/**
 * Body PATCH /admin/payments/gateway (@UC9050, AC1) — đặt mode toàn cục + reason (audit).
 * `.strict()` chặn field lạ. reason optional (ghi vào audit diff, no-secret).
 */
export const gatewayToggleRequestSchema = z
  .object({
    mode: gatewayModeSchema,
    reason: z.string().max(GATEWAY_TOGGLE_REASON_MAX_LENGTH).optional(),
  })
  .strict();
export type GatewayToggleRequest = z.infer<typeof gatewayToggleRequestSchema>;

/** Trạng thái gateway hiện tại (GET + phản hồi PATCH). `gatewayEnabled` = (mode === gateway_auto). */
export const gatewayStatusSchema = z
  .object({
    mode: gatewayModeSchema,
    gatewayEnabled: z.boolean(),
  })
  .strict();
export type GatewayStatus = z.infer<typeof gatewayStatusSchema>;

// ── Webhook monitor (@UC9051) ───────────────────────────────────────────────

/** Max page-size bảng webhook-events. Default (20) do backend áp khi vắng. */
export const ADMIN_WEBHOOK_EVENTS_MAX_LIMIT = 100;

/**
 * Trạng thái xử lý 1 webhook event (khớp model GatewayWebhookEvent.status):
 *  - `received`  : đã nhận (chưa verify xong).
 *  - `verified`  : chữ ký hợp lệ (chưa post ledger).
 *  - `processed` : đã post CONTRIBUTION (confirm).
 *  - `failed`    : lỗi xử lý (chữ ký sai / không match / nghiệp vụ) — KHÔNG post ledger.
 *  - `duplicate` : trùng externalId (dedupe — không post lại).
 */
export const webhookEventStatusSchema = z.enum([
  "received",
  "verified",
  "processed",
  "failed",
  "duplicate",
]);
export type WebhookEventStatus = z.infer<typeof webhookEventStatusSchema>;

/**
 * Money string trên wire = BigInt minor units (integer). Reject float & rác. `null` khi không
 * parse được số tiền từ payload webhook (vẫn ghi event để giám sát).
 */
const moneyMinorNullable = z
  .string()
  .regex(/^-?\d+$/, "money phải là số nguyên minor units (không float)")
  .nullable();

/**
 * Một dòng webhook-event (UC9051 AC2-AC4). READ-ONLY, KHÔNG payload/secret thô.
 *  - `signatureValid` (AC2) — false = chữ ký sai → UI badge alert.
 *  - `dedupeHit` (AC3) — true = trùng externalId (bị bỏ qua).
 *  - `latencyMs` + `slaBreached` (AC4) — độ trễ receivedAt→processedAt; slaBreached = latencyMs > SLA.
 */
export const webhookEventListItemSchema = z
  .object({
    id: z.string(),
    provider: z.string(),
    externalId: z.string(),
    eventType: z.string().nullable(),
    status: webhookEventStatusSchema,
    signatureValid: z.boolean(),
    dedupeHit: z.boolean(),
    /** Hash payload (SHA-256) — KHÔNG payload thô. */
    payloadHash: z.string(),
    /** Số tiền minor units (string) hoặc null nếu không parse được. */
    amountMinor: moneyMinorNullable,
    /** Thông điệp lỗi (no-PII) khi status=failed; null nếu không lỗi. */
    error: z.string().nullable(),
    receivedAt: z.string(),
    processedAt: z.string().nullable(),
    /** Độ trễ receivedAt→processedAt (ms); null nếu chưa processed. */
    latencyMs: z.number().int().min(0).nullable(),
    /** true nếu latencyMs > SLA (config). AC4 cảnh báo lag. */
    slaBreached: z.boolean(),
  })
  .strict();
export type WebhookEventListItem = z.infer<typeof webhookEventListItemSchema>;

/** Query GET /admin/payments/webhook-events — filter status + cursor pagination. */
export const webhookEventsQuerySchema = z
  .object({
    status: webhookEventStatusSchema.optional(),
    cursor: z.string().max(512).optional(),
    limit: z.coerce.number().int().min(1).max(ADMIN_WEBHOOK_EVENTS_MAX_LIMIT).optional(),
  })
  .strict();
export type WebhookEventsQuery = z.infer<typeof webhookEventsQuerySchema>;

/**
 * Envelope bảng webhook-events `{ data, pageInfo, slaSeconds }`. `slaSeconds` = ngưỡng SLA (giây)
 * đọc từ system_settings — null nếu chưa cấu hình (slaBreached luôn false). KHÔNG hardcode ở client.
 */
export const webhookEventsResponseSchema = z
  .object({
    data: z.array(webhookEventListItemSchema),
    pageInfo: z.object({
      hasNext: z.boolean(),
      nextCursor: z.string().nullable(),
    }),
    slaSeconds: z.number().int().min(0).nullable(),
  })
  .strict();
export type WebhookEventsResponse = z.infer<typeof webhookEventsResponseSchema>;

// ── Deadletter + reprocess (US-AE6.F3-0022 · @UC9052 retry/deadletter · @UC9053 replay) ──────────

/**
 * Số tiền minor units (string) hoặc null — dùng chung cho deadletter + reconcile DTO. Reject float/rác
 * (regex integer). B1 no-float trên wire.
 */
const moneyMinorNullableStr = z
  .string()
  .regex(/^-?\d+$/, "money phải là số nguyên minor units (không float)")
  .nullable();

/**
 * Một dòng deadletter (US-AE6.F3-0022 · @UC9052) — webhook event FAILED đã vào deadletter (fail quá
 * max retry) chờ admin reprocess thủ công. READ-ONLY, KHÔNG payload/secret thô (chỉ payloadHash).
 *  - `retryCount` — số lần đã retry (tới max → deadletter).
 *  - `lastError` — lỗi lần retry gần nhất (no-PII/secret) để admin chẩn đoán.
 *  - `deadLetteredAt` — mốc vào deadletter (non-null vì đây là danh sách deadletter).
 */
export const deadletterItemSchema = z
  .object({
    id: z.string(),
    provider: z.string(),
    externalId: z.string(),
    eventType: z.string().nullable(),
    status: webhookEventStatusSchema,
    retryCount: z.number().int().min(0),
    lastError: z.string().nullable(),
    amountMinor: moneyMinorNullableStr,
    receivedAt: z.string(),
    deadLetteredAt: z.string().nullable(),
    /** Hash payload (SHA-256) — KHÔNG payload thô (C2/G4). */
    payloadHash: z.string(),
  })
  .strict();
export type DeadletterItem = z.infer<typeof deadletterItemSchema>;

/** Query GET /admin/payments/deadletter — cursor pagination (không filter status; luôn deadletter). */
export const deadletterQuerySchema = z
  .object({
    cursor: z.string().max(512).optional(),
    limit: z.coerce.number().int().min(1).max(ADMIN_WEBHOOK_EVENTS_MAX_LIMIT).optional(),
  })
  .strict();
export type DeadletterQuery = z.infer<typeof deadletterQuerySchema>;

/** Envelope list deadletter `{ data, pageInfo }`. */
export const deadletterResponseSchema = z
  .object({
    data: z.array(deadletterItemSchema),
    pageInfo: z.object({
      hasNext: z.boolean(),
      nextCursor: z.string().nullable(),
    }),
  })
  .strict();
export type DeadletterResponse = z.infer<typeof deadletterResponseSchema>;

/**
 * Kết quả POST /admin/payments/webhooks/:id/reprocess (US-AE6.F3-0022 · @UC9052/@UC9053 · AC2 idempotent).
 *  - `reprocessed` — true = đã chạy lại pipeline (post mới hoặc no-op idempotent).
 *  - `duplicate`  — true = event ĐÃ post CONTRIBUTION trước đó → KHÔNG tạo trùng (idempotent no-op).
 *                    UI hiển thị "đã xử lý (không tạo trùng)" thay vì báo lỗi.
 *  - `blocked`    — true = replay-guard chặn (ngoài window ±5' / event-id lặp nghi vấn) → KHÔNG post +
 *                    alert security (@UC9053). `blockedReason` giải thích ngắn (no-secret).
 *  - `ledgerTxId` — tx CONTRIBUTION liên quan (post mới hoặc đã có); null nếu không post/blocked.
 */
export const reprocessResultSchema = z
  .object({
    id: z.string(),
    reprocessed: z.boolean(),
    duplicate: z.boolean(),
    blocked: z.boolean(),
    blockedReason: z.string().nullable(),
    ledgerTxId: z.string().nullable(),
    /**
     * FIX-1273b (HIGH-1 review #1273): phần DƯ của event KHÔNG vào sổ (nhóm chặn đóng dư / suất không còn hoạt động) — STRING minor, đọc từ dấu
     * bền (outbox "dư chờ hoàn"); null nếu không có. Còn việc đối soát/hoàn — admin không được thấy "đã xử lý" trơn.
     */
    overpayBlockedMinor: z.string().regex(/^[0-9]+$/).nullable(),
  })
  .strict();
export type ReprocessResult = z.infer<typeof reprocessResultSchema>;

// ── Gateway reconcile (US-AE6.F3-0022 · @UC9054 gateway-reconciliation) ──────────────────────────

/**
 * Trạng thái đối soát 1 giao dịch sao kê gateway vs ledger:
 *  - `matched`              — khớp cả statementRef + amount với 1 ledger CONTRIBUTION.
 *  - `missing_in_ledger`    — có trên sao kê, KHÔNG có ledger (tiền vào gateway nhưng chưa ghi sổ → điều tra).
 *  - `missing_in_statement` — có ledger CONTRIBUTION gateway_auto, KHÔNG có trên sao kê (đối chiếu ngược).
 *  - `amount_mismatch`      — khớp ref nhưng lệch số tiền.
 */
export const reconcileDiffStatusSchema = z.enum([
  "matched",
  "missing_in_ledger",
  "missing_in_statement",
  "amount_mismatch",
]);
export type ReconcileDiffStatus = z.infer<typeof reconcileDiffStatusSchema>;

/**
 * Một dòng đối soát (US-AE6.F3-0022 · @UC9054). Money = minor units string (B1 no-float). READ-ONLY —
 * KHÔNG sửa ledger (như AF5.6). `investigatedAt` null = chưa điều tra.
 */
export const reconcileDiffItemSchema = z
  .object({
    id: z.string(),
    provider: z.string(),
    statementRef: z.string(),
    /** Số tiền trên sao kê (minor units string). */
    statementAmountMinor: z
      .string()
      .regex(/^-?\d+$/, "money phải là số nguyên minor units (không float)"),
    currency: z.string(),
    ledgerTxId: z.string().nullable(),
    status: reconcileDiffStatusSchema,
    investigatedAt: z.string().nullable(),
    createdAt: z.string(),
  })
  .strict();
export type ReconcileDiffItem = z.infer<typeof reconcileDiffItemSchema>;

/** Query GET /admin/payments/gateway-reconcile — filter status + cursor pagination. */
export const reconcileQuerySchema = z
  .object({
    status: reconcileDiffStatusSchema.optional(),
    cursor: z.string().max(512).optional(),
    limit: z.coerce.number().int().min(1).max(ADMIN_WEBHOOK_EVENTS_MAX_LIMIT).optional(),
  })
  .strict();
export type ReconcileQuery = z.infer<typeof reconcileQuerySchema>;

/**
 * Envelope list đối soát `{ data, pageInfo, summary }`. `summary` = đếm theo status (dashboard nhanh).
 */
export const reconcileResponseSchema = z
  .object({
    data: z.array(reconcileDiffItemSchema),
    pageInfo: z.object({
      hasNext: z.boolean(),
      nextCursor: z.string().nullable(),
    }),
    summary: z.object({
      matched: z.number().int().min(0),
      missingInLedger: z.number().int().min(0),
      missingInStatement: z.number().int().min(0),
      amountMismatch: z.number().int().min(0),
    }),
  })
  .strict();
export type ReconcileResponse = z.infer<typeof reconcileResponseSchema>;
